import React, { createContext, useContext, useState, useEffect } from "react"
import { User } from "firebase/auth"
import { DocumentData, doc, onSnapshot } from "firebase/firestore"
import { db } from "@/config/firebase"
import { subscribeToAuthState } from "@/shared/services/auth"

interface AuthContextType {
  user: User | null
  userDetails: DocumentData | null
  loading: boolean
  setUserDetails: React.Dispatch<React.SetStateAction<DocumentData | null>>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userDetails: null,
  loading: true,
  setUserDetails: () => {}
})

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [userDetails, setUserDetails] = useState<DocumentData | null>(null)

  useEffect(() => {
    let unsubscribeSnapshot: (() => void) | null = null

    const unsubscribeAuth = subscribeToAuthState(data => {
      // Clean up any previous Firestore listener when auth state changes
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot()
        unsubscribeSnapshot = null
      }

      setLoading(true)
      setUser(data)

      if (data) {
        // posthog.identify(data.uid, {
        //   email: data.email ?? undefined,
        //   name: data.displayName ?? undefined
        // })

        const userRef = doc(db, "users", data.uid)
        unsubscribeSnapshot = onSnapshot(
          userRef,
          snapshot => {
            if (snapshot.exists()) {
              setUserDetails(snapshot.data())
            } else {
              setUserDetails(null)
            }
            setLoading(false)
          },
          error => {
            console.error("Error listening to user details:", error)
            setLoading(false)
          }
        )
      } else {
        // posthog.reset()
        setUserDetails(null)
        setLoading(false)
      }
    })

    return () => {
      unsubscribeAuth()
      if (unsubscribeSnapshot) unsubscribeSnapshot()
    }
  }, [])

  return <AuthContext.Provider value={{ user, userDetails, loading, setUserDetails }}>{children}</AuthContext.Provider>
}

const useAuth = (): AuthContextType => useContext(AuthContext)

export { AuthProvider, useAuth }
export type { AuthContextType }
